import axios from 'axios'
import type { AxiosError, AxiosInstance } from 'axios'

interface ApiErrorBody {
  error?: string
}

export class ApiError extends Error {
  readonly status: number | undefined

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong'
}

export const API_BASE = '/api'

const SELF_HANDLED_401 = ['/auth/', '/user/me', '/user/delete', '/user/delete-code', '/ar/']

function isAuthPath(path: string): boolean {
  return SELF_HANDLED_401.some((prefix) => path.startsWith(prefix))
}

let redirecting = false

async function onSessionExpired(): Promise<void> {
  if (redirecting) return
  redirecting = true
  try {
    const [{ useAuthStore }, { default: router }] = await Promise.all([
      import('@/stores/auth'),
      import('@/router'),
    ])
    useAuthStore().clearSession()

    const current = router.currentRoute.value
    if (current.name !== 'login') {
      await router.replace({ name: 'login', query: { redirect: current.fullPath } })
    }
  } catch {
    window.location.assign('/login')
  } finally {
    redirecting = false
  }
}

function createClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  })

  client.interceptors.response.use(
    (res) => res,
    (err: AxiosError<ApiErrorBody>) => {
      const path = err.config?.url ?? ''
      if (err.response?.status === 401 && !isAuthPath(path)) {
        void onSessionExpired()
      }
      return Promise.reject(
        new ApiError(
          err.response?.data?.error || err.message || 'Something went wrong',
          err.response?.status,
        ),
      )
    },
  )

  return client
}

const http = createClient(API_BASE)

export default http
